import React, { useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import "../sda/styles.scss";
import "./styles.scss";
import { cardBackgroundColor } from "../../config/palette";
import { Text } from "@amzn/stencil-react-components/text";
import { Link } from "@amzn/stencil-react-components/link";
import {
  TooltipAlignment,
  withTooltip,
} from "@amzn/stencil-react-components/tooltip";
import { IconQuestionCircle } from "@amzn/stencil-react-components/icons";
import calendar from "./calendar.png";
import { TriggerButton } from "@amzn/stencil-react-components/helpers";
import { ExpanderContent } from "@amzn/stencil-react-components/expander";
import { Card } from "@amzn/stencil-react-components/card";
import { Button } from "@amzn/stencil-react-components/button";
import { processTime } from "../../utility/time-processor";
import useArb from "../../hooks/use-arb";

interface StartDateCardHeaderProps {
  startDate?: string;
  startTime?: string;
  address?: string;
  startDateStatus?: string;
  onViewDetailsClick: () => void;
}

const IconWithTooltip = withTooltip()(IconQuestionCircle);

export const StartDateCardHeader = ({
  startDate,
  startTime,
  address,
  startDateStatus,
  onViewDetailsClick,
}: StartDateCardHeaderProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const bundle = useArb("HVHChecklistPantherResource");

  const googlePlaceURL = "http://maps.google.com/?q=";

  const viewButtonText = bundle.getMessage("Checklist-StartDate-ButtonText");
  const addressLink = (
    <Link
      fontSize={"T200"}
      href={googlePlaceURL + address}
      target={"_blank"}
      className={"link-style"}
    >
      {" "}
      {address}{" "}
    </Link>
  );
  const addressDetails = bundle.formatMessage("Checklist-StartDate-Address", {
    address: addressLink,
  });

  //ASH will process the startDate from SDA, as the returned startDate contains weekday. ASH displays day and month only on UI.
  const currDate = new Date(); //SDA doesn't return year, so we have to create on our end
  const tokens = startDate && startDate.split(" "); //Return value from SDA: Wed Dec 30
  const date = tokens && tokens.length >= 3 && tokens[1];
  const day = tokens && tokens.length >= 3 && tokens[2];
  const processedStartTime = processTime(startTime);
  //reason why ASH has to process time and date is because NHE doesnt handle the format by locale. we will need to format it into a valid date object to be translated by panther
  const dateWithTimeString =
    date &&
    date + " " + day + " " + currDate.getFullYear() + " " + processedStartTime;
  const dateWithTime = dateWithTimeString && new Date(dateWithTimeString);

  //if SDA returns an invalid date, dateWithTime will be derived as invalid, this exception will be caught by formatMessage
  const tentativeFirstDayPattern = bundle.formatMessage(
    "Checklist-StartDate-TentativeDate",
    {
      dateParam: dateWithTime,
    }
  );

  const confirmedFirstDayPattern = bundle.formatMessage(
    "Checklist-StartDate-ConfirmedDate",
    {
      dateParam: dateWithTime,
    }
  );

  const monthFormatted = bundle.formatMessage(
    "Checklist-AppointmentCard-MonthUpperCase",
    {
      dateParam: dateWithTime,
    }
  );

  const dayFormatted = bundle.formatMessage("Checklist-AppointmentCard-Day", {
    dateParam: dateWithTime,
  });

  const tootTip = bundle.getMessage("Checklist-StartDate-ToolTip");
  const message =
    startDateStatus === "TENTATIVE" ? (
      <Text fontSize="T300" lineHeight="2">
        {tentativeFirstDayPattern}
        <span className="tooltip-icon-in-text">
          <IconWithTooltip
            isInline={true}
            tooltipAlignment={TooltipAlignment.Center}
            tooltipPosition="bottom"
            tooltipText={tootTip}
          />
        </span>{" "}
      </Text>
    ) : (
      <Text fontSize="T300" lineHeight="2">
        {confirmedFirstDayPattern}
      </Text>
    );

  return (
    <Card justifyContent="center" padding="0">
      <Col width="100%">
        <Row
          backgroundColor={cardBackgroundColor}
          justifyContent="center"
          alignItems="center"
          gridGap="1rem"
          padding={"1rem"}
        >
          <Col justifyContent="center">
            <img
              src={calendar}
              alt="Calendar"
              width="80px"
              aria-hidden="true"
            />
            <Text className="calendar-day" fontSize="35px">
              {dayFormatted}
            </Text>
            <Text className="calendar-month" fontSize="12px">
              {monthFormatted}
            </Text>
          </Col>

          <Col maxWidth={220}>{message}</Col>

          {startDateStatus !== "TENTATIVE" && (
            <Col>
              <TriggerButton
                iconAltText="Information about first date"
                isExpanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
              />
            </Col>
          )}
        </Row>
        <ExpanderContent isExpanded={isExpanded}>
          <Col gridGap="1rem" padding="1rem">
            <Row justifyContent="center" alignItems="center">
              <Col maxWidth={368} gridGap="1rem">
                <Row>
                  <Text fontSize="T200" lineHeight="1.5" textAlign="justify">
                    {addressDetails}
                  </Text>
                </Row>
                <Row>
                  <Button onClick={onViewDetailsClick}>{viewButtonText}</Button>
                </Row>
              </Col>
            </Row>
          </Col>
        </ExpanderContent>
      </Col>
    </Card>
  );
};
